import clsx from 'clsx';
import { forwardRef, useEffect, useReducer, useRef, useState } from 'react';

import {
  computeAllChipsFitExpanded,
  findOverflowChipIndex,
} from './chip-group-utils.js';
import { chipGroupCSS } from './ChipGroup.sty.js';
import { ChipGroupContext } from './contexts/ChipGroupContext.js';
import { InternalChipGroupContext } from './contexts/InternalChipGroupContext.js';
import { Control } from './Control.js';
import { HiddenCount } from './HiddenCount.js';
import { chipGroupReducer } from './state/chip-group-state-reducer.js';
import { useAriaLabelingProps } from '../../core/hooks/useAriaLabelingProps.js';
import { useBehavioralTrackingProps } from '../../core/hooks/useBehavioralTrackingProps.js';
import { useContainerSize } from '../../core/hooks/useContainerSize.js';
import { useControllableState } from '../../core/hooks/useControllableState.js';
import { useId } from '../../core/hooks/useId.js';
import { useMergeRefs } from '../../core/hooks/useMergeRefs.js';
import { useSafeForwardProps } from '../../core/hooks/useSafeForwardProps.js';
import type { AriaLabelingProps } from '../../core/types/a11y-props.js';
import type { BehaviorTrackingProps } from '../../core/types/behavior-tracking-props.js';
import type { DataTestId } from '../../core/types/data-props.js';
import type { MaskingProps } from '../../core/types/masking-props.js';
import type { StylingProps } from '../../core/types/styling-props.js';
import type { WithChildren } from '../../core/types/with-children.js';
import { Flex } from '../../layouts/flex/Flex.js';
import type { ChipOwnProps } from '../chip/types/chip.js';

const COMPONENT_NAME = 'ChipGroup';

/**
 * Accepted base properties for ChipGroup.
 * @public
 */
export interface ChipGroupBaseProps
  extends StylingProps,
    DataTestId,
    MaskingProps,
    AriaLabelingProps,
    BehaviorTrackingProps {
  /**
   * Whether the chip group is disabled.
   * If set to true, all the chips inside the group are disabled, unless explicitly overridden.
   * @defaultValue false
   */
  disabled?: boolean;

  /**
   * The size of the spacing and chips in the group.
   * All the chips match the size of the group, but can be overridden individually.
   * @defaultValue default
   */
  size?: ChipOwnProps['size'];

  /**
   * Callback triggered when the expanded state changes.
   */
  onExpandedChange?: (state: boolean) => void;

  /**
   * Maximum number of chips rendered before the "Show more" control.
   *
   * If set to 'auto', the chip group automatically determines
   * how many chips can fit based on the available width.
   * If a number is provided, it limits the visible chips to that number
   * when the group is collapsed, regardless of available space.
   * @defaultValue 'auto'
   */
  maxVisibleChips?: 'auto' | number;
}

/**
 * Properties for the controlled variant of ChipGroup.
 * @public
 */
export interface ChipGroupControlledProps {
  /** Whether the chip group is expanded. */
  expanded?: boolean;
  /** Whether the chip group is expanded by default. */
  defaultExpanded?: never;
}

/**
 * Properties for the uncontrolled variant of ChipGroup.
 * @public
 */
export interface ChipGroupUncontrolledProps {
  /** Whether the chip group is expanded. */
  expanded?: never;
  /** Whether the chip group is expanded by default. */
  defaultExpanded?: boolean;
}

/**
 * Properties for the ChipGroup component.
 * @public
 */
export type ChipGroupProps = ChipGroupBaseProps &
  (ChipGroupControlledProps | ChipGroupUncontrolledProps) &
  WithChildren;

/**
 * ChipGroup component
 */
const RootChipGroup = forwardRef<HTMLElement, ChipGroupProps>(
  (props, forwardedRef) => {
    const {
      children,
      expanded: expandedProp,
      defaultExpanded,
      disabled,
      size,
      onExpandedChange,
      maxVisibleChips = 'auto',
      className: consumerClassName,
      style: consumerStyle,
      'data-testid': dataTestId,
      'data-dtrum-mask': dataDtrumMask,
      'data-dtrum-allow': dataDtrumAllow,
      ...remainingProps
    } = props;

    const [behavioralTrackingProps, restProps] =
      useBehavioralTrackingProps(remainingProps);
    const ariaLabelingProps = useAriaLabelingProps(restProps);
    const forwardProps = useSafeForwardProps(restProps, ariaLabelingProps);

    const uniqueId = useId();

    const [state, dispatch] = useReducer(chipGroupReducer, {
      chips: {},
    });

    const containerRef = useRef<HTMLDivElement | null>(null);
    const rootRef = useMergeRefs<HTMLElement | null>([
      containerRef,
      forwardedRef,
    ]);

    // Width of the chip group container.
    const { width: containerWidth } = useContainerSize(containerRef);

    const [expanded, setExpanded] = useControllableState({
      value: expandedProp,
      defaultValue: defaultExpanded,
      onChange: onExpandedChange,
    });

    const [allChipsFit, setAllChipsFit] = useState(false);
    const [hiddenChips, setHiddenChips] = useState<string[]>([]);
    const [visibleChips, setVisibleChips] = useState<string[]>([]);

    useEffect(() => {
      const chipEntries = Object.entries(state.chips);
      const chipEntriesKeys = chipEntries.map(
        ([uniqueChipKey]) => uniqueChipKey,
      );

      // Reset visibility when there are no chips.
      if (chipEntries.length === 0) {
        setVisibleChips([]);
        return;
      }

      // Cannot calculate visibility when any chip has not been measured yet.
      if (chipEntries.some(([, chip]) => !chip.width)) {
        return;
      }

      if (expanded) {
        setHiddenChips([]);
        setVisibleChips(chipEntriesKeys);

        // When `expanded` is controlled, the consumer owns the toggle and renders the
        // control so users can collapse again. Auto-hiding it because everything currently
        // fits would remove their control, so the auto-hide behaviour only applies in
        // uncontrolled mode where the component manages expansion itself.
        if (expandedProp !== undefined) {
          setAllChipsFit(false);
          return;
        }

        setAllChipsFit(
          computeAllChipsFitExpanded(
            chipEntries,
            containerWidth,
            size,
            state.control?.width ?? 0,
            state.control?.show,
            maxVisibleChips,
          ),
        );
        return;
      }

      const fittingIndex = findOverflowChipIndex(
        chipEntries,
        containerWidth,
        size,
        state.control?.width || 0,
        state.control?.show,
      );

      // Apply maxVisibleChips limit if specified and valid (non-negative)
      const effectiveHiddenChipsIndex =
        maxVisibleChips !== 'auto' && maxVisibleChips >= 0
          ? Math.min(fittingIndex, maxVisibleChips)
          : fittingIndex;

      setAllChipsFit(effectiveHiddenChipsIndex === chipEntries.length);
      setHiddenChips(chipEntriesKeys.slice(effectiveHiddenChipsIndex));
      setVisibleChips(chipEntriesKeys.slice(0, effectiveHiddenChipsIndex));
    }, [containerWidth, expanded, expandedProp, size, state, maxVisibleChips]);

    return (
      <ChipGroupContext.Provider
        value={{
          visibleKeys: visibleChips,
          hiddenKeys: hiddenChips,
        }}
      >
        <InternalChipGroupContext.Provider
          value={{
            chipGroupId: uniqueId,
            disabled,
            size,
            expanded,
            allChipsFit,
            dispatch,
            setExpanded,
          }}
        >
          <Flex
            ref={rootRef}
            id={uniqueId}
            className={clsx(
              chipGroupCSS({ size, expanded }),
              consumerClassName,
            )}
            style={consumerStyle}
            data-testid={dataTestId}
            data-dtrum-mask={dataDtrumMask}
            data-dtrum-allow={dataDtrumAllow}
            {...ariaLabelingProps}
            {...behavioralTrackingProps}
            {...forwardProps}
            data-dt-component={COMPONENT_NAME}
          >
            {children}
          </Flex>
        </InternalChipGroupContext.Provider>
      </ChipGroupContext.Provider>
    );
  },
);

(RootChipGroup as typeof RootChipGroup & { displayName: string }).displayName =
  COMPONENT_NAME;

/**
 * Use the `ChipGroup` to group multiple `Chip` components in an expandable container.
 * @public
 */
export const ChipGroup = Object.assign(RootChipGroup, {
  Control,
  HiddenCount,
});
